import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Search, MapPin, Crosshair, Loader2 } from 'lucide-react';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon for selected location
const selectedIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="background-color: #00B5A7; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 4px solid white; box-shadow: 0 4px 10px rgba(0,0,0,0.3);">
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

// Tunis center coordinates
const TUNIS_CENTER = [36.8065, 10.1815];

// Component to handle map click events
const LocationMarker = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position ? (
    <Marker position={position} icon={selectedIcon} />
  ) : null;
};

// Component to fly to a position
const MapController = ({ flyTo }) => {
  const map = useMap();
  
  useEffect(() => {
    if (flyTo) {
      map.flyTo(flyTo, 16, { duration: 1 });
    }
  }, [flyTo, map]);
  
  return null;
};

export default function LocationPicker({ 
  latitude, 
  longitude, 
  address,
  onLocationChange, 
  onAddressChange 
}) {
  const [position, setPosition] = useState(
    latitude && longitude ? [latitude, longitude] : null
  );
  const [searchQuery, setSearchQuery] = useState(address || '');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [flyTo, setFlyTo] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Update parent when position changes
  useEffect(() => {
    if (position) {
      onLocationChange(position[0], position[1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  // Debounced address search using OpenStreetMap Nominatim
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      // Add Tunisia as context for better results
      const searchTerm = query.toLowerCase().includes('tunis') ? query : `${query}, Tunisia`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchTerm)}&limit=5&addressdetails=1`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Address search failed:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    onAddressChange(value);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(value);
    }, 500);
  };

  // Handle selecting a search result
  const handleSelectResult = (result) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    setPosition([lat, lon]);
    setFlyTo([lat, lon]);
    setSearchQuery(result.display_name);
    onAddressChange(result.display_name);
    setSearchResults([]);
  };

  // Get current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setSearching(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          setPosition([lat, lon]);
          setFlyTo([lat, lon]);
          setSearching(false);
          
          // Reverse geocode to get address
          fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`)
            .then(res => res.json())
            .then(data => {
              if (data.display_name) {
                setSearchQuery(data.display_name);
                onAddressChange(data.display_name);
              }
            })
            .catch(() => {});
        },
        (error) => {
          console.error('Geolocation error:', error);
          setSearching(false);
        },
        { enableHighAccuracy: true }
      );
    }
  };

  return (
    <div className="space-y-3">
      <Label className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-tunis-teal" />
        Standort auswählen *
      </Label>
      
      {/* Address Search */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Adresse eingeben oder auf Karte klicken..."
              className="pl-10"
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={getCurrentLocation}
            title="Meinen Standort verwenden"
          >
            <Crosshair className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Search Results Dropdown */}
        {searchResults.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
            {searchResults.map((result, index) => (
              <button
                key={index}
                type="button"
                className="w-full px-4 py-3 text-left hover:bg-slate-50 border-b last:border-b-0 text-sm"
                onClick={() => handleSelectResult(result)}
              >
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-tunis-teal mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{result.display_name}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Map */}
      <div className="h-[300px] rounded-lg overflow-hidden border border-slate-200">
        <MapContainer
          center={position || TUNIS_CENTER}
          zoom={position ? 15 : 11}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />
          <LocationMarker position={position} setPosition={setPosition} />
          <MapController flyTo={flyTo} />
        </MapContainer>
      </div>
      
      <p className="text-xs text-muted-foreground">
        💡 Klicken Sie auf die Karte, um den genauen Standort zu markieren, oder nutzen Sie die Adresssuche.
      </p>
      
      {/* Coordinates Display */}
      {position && (
        <div className="flex gap-4 text-sm text-muted-foreground bg-slate-50 px-3 py-2 rounded">
          <span>📍 Lat: {position[0].toFixed(6)}</span>
          <span>Lng: {position[1].toFixed(6)}</span>
        </div>
      )}
    </div>
  );
}
